import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { apiList } from "@/lib/api";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";
import { Comissao } from "@/hooks/useComissoes";
import { AgendamentoPerformance, calcularMeta, OrdemServicoPerformance, producaoDentistaCompleta, statusRealizado } from "@/lib/performance";
import { parseLocalDateTime, toLocalISODate, todayLocalDate } from "@/lib/date-utils";

export interface DentistaPerformance {
  dentista_id: string;
  dentista_nome: string;
  total_procedimentos: number;
  faturamento_total: number;
  total_comissao: number;
  crescimento: string;
  percentual_performance: number;
}

export interface ProcedimentoDentista {
  dentista_id: string;
  dentista_nome: string;
  procedimento: string;
  quantidade: number;
  valor_total: number;
}

type DentistaRelatorio = {
  id: string;
  nome: string;
  status?: string;
  recebe_meta?: boolean;
  meta_valor?: number;
  meta_bonus?: number;
};

export const useGanhoDentista = (dataInicio?: Date, dataFim?: Date) => {
  const { user } = useAuth();
  const activeClinicaId = useActiveClinicaId();
  const [loading, setLoading] = useState(true);
  const [performanceDentistas, setPerformanceDentistas] = useState<DentistaPerformance[]>([]);
  const [topProcedimentos, setTopProcedimentos] = useState<ProcedimentoDentista[]>([]);

  const fetchRelatorio = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const [dentistasRows, agendamentosRows, ordensServicoRows, comissoesRows] = await Promise.all([
        apiList<DentistaRelatorio>("dentistas"),
        apiList<AgendamentoPerformance>("agendamentos"),
        apiList<OrdemServicoPerformance>("ordem_servico"),
        apiList<Comissao>("comissoes"),
      ]);

      const currentDate = todayLocalDate();
      const inicio = dataInicio || new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const fim = dataFim || new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
      const inicioIso = toLocalISODate(inicio);
      const fimIso = toLocalISODate(fim);
      const dentistas = dentistasRows.filter((item) => (item.status || "Ativo") !== "Inativo");
      const realizados = agendamentosRows.filter((item) => {
        const dataAgendamento = parseLocalDateTime(item.data_agendamento);
        return statusRealizado(item.status) && dataAgendamento >= inicio && dataAgendamento <= fim;
      });
      const comissoes = comissoesRows.filter((item) => item.status === "Paga" && String(item.data_inicio || "") <= fimIso && String(item.data_fim || "") >= inicioIso);

      const performance: DentistaPerformance[] = dentistas.map((dentista) => {
        const agendamentosDentista = realizados.filter((item) => item.dentista_id === dentista.id);
        const faturamentoTotal = producaoDentistaCompleta(agendamentosRows, ordensServicoRows, dentista.id, inicio, fim);
        const totalComissao = comissoes
          .filter((item) => item.dentista_id === dentista.id)
          .reduce((sum, item) => sum + Number(item.valor_comissao || 0), 0);
        const meta = calcularMeta(dentista, faturamentoTotal);

        return {
          dentista_id: dentista.id,
          dentista_nome: dentista.nome,
          total_procedimentos: agendamentosDentista.length,
          faturamento_total: faturamentoTotal,
          total_comissao: totalComissao,
          crescimento: "+0%",
          percentual_performance: meta.valor > 0 ? meta.percentual : 0,
        };
      });

      const procedimentosMap = new Map<string, ProcedimentoDentista>();
      realizados.forEach((agendamento) => {
        const dentista = dentistas.find((item) => item.id === agendamento.dentista_id);
        if (!dentista) return;
        const procedimento = agendamento.procedimento || "Procedimento";
        const key = `${agendamento.dentista_id}-${procedimento}`;
        const current = procedimentosMap.get(key);
        if (current) {
          current.quantidade += 1;
          current.valor_total += Number(agendamento.valor || 0);
        } else {
          procedimentosMap.set(key, {
            dentista_id: agendamento.dentista_id || "",
            dentista_nome: dentista.nome,
            procedimento,
            quantidade: 1,
            valor_total: Number(agendamento.valor || 0),
          });
        }
      });

      setPerformanceDentistas(performance);
      setTopProcedimentos(Array.from(procedimentosMap.values()).sort((a, b) => b.valor_total - a.valor_total).slice(0, 10));
    } catch (error: any) {
      console.error("Erro ao buscar relatório:", error);
      toast.error("Erro ao carregar relatório de ganho por dentista");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRelatorio();
  }, [user?.id, activeClinicaId, dataInicio?.getTime(), dataFim?.getTime()]);

  return {
    performanceDentistas,
    topProcedimentos,
    loading,
    refetch: fetchRelatorio,
  };
};
